/**
 * Sistema di espansione e selezione per il FilterHandler
 *
 * @description
 * La classe ExpandSelect gestisce l'espansione di proprietà correlate e la selezione
 * di campi specifici nelle query OData:
 * - Espansione di relazioni nidificate ($expand)
 * - Selezione di proprietà specifiche ($select)
 * - Costruzione automatica di query OData complesse
 */

import type { ODataContext, ExpandDefinition } from '../types';

/**
 * Interfaccia per dati di espansione e selezione
 * Utilizzabile per Vue 3 Composition API con oggetti plain reattivi
 */
export interface IExpandSelectData {
	/** Proprietà root da selezionare */
	rootSelect: string | null;
	/** Array delle espansioni attive */
	expansions: Array<{ path: string; select?: string }>;
}

/**
 * Tipo combinato per espansioni con metodi fluenti
 */
export type ExpandSelectFactory = IExpandSelectData & {
	/** Aggiunge una espansione */
	expand(path: string, select?: string): ExpandSelectFactory;
	/** Rimuove una espansione */
	removeExpand(path: string): ExpandSelectFactory;
	/** Pulisce tutte le espansioni */
	clear(): ExpandSelectFactory;
	/** Imposta la selezione root */
	select(fields: string): ExpandSelectFactory;
	/** Verifica se ha espansioni attive */
	hasExpansions(): boolean;
};

/**
 * Classe per la gestione di espansioni e selezioni OData
 *
 * @description
 * ExpandSelect fornisce un'API fluente per costruire query OData complesse
 * con espansioni di relazioni e selezioni di campi specifici. Supporta
 * espansioni nidificate multiple e ottimizzazione automatica delle query.
 *
 * @example
 * // Espansione semplice
 * const expandSelect = new ExpandSelect()
 *   .expand('customer')               // Espande la relazione customer
 *   .expand('order.items');           // Espande order e i suoi items
 *
 * @example
 * // Con selezione di proprietà root
 * const expandSelect = new ExpandSelect('id,name,createdAt')
 *   .expand('customer', 'id,companyName')     // Espande customer con campi specifici
 *   .expand('order.items', 'id,quantity,price');
 *
 * @example
 * // Uso con filterHandler
 * const dataExpand = new ExpandSelect()
 *   .expand('order.customer')         // Include i dati del cliente dell'ordine
 *   .expand('activity')               // Include i dati dell'attività
 *   .expand('worker.user');           // Include i dati utente del worker
 *
 * // Nel buildOdataQuery(), questo genererà:
 * // $expand=order($expand=customer),activity,worker($expand=user)
 *
 * @category Core
 * @since 1.0.0
 */
export class ExpandSelect {
	/**
	 * Proprietà root da selezionare (per $select)
	 * @private
	 */
	private _propertiesToSelect: string = '';

	/**
	 * Nome della relazione corrente (per costruzione nidificata)
	 * @private
	 */
	private _relation: string = '';

	/**
	 * Array delle relazioni da espandere
	 * @private
	 */
	private _relations: ExpandSelect[] = [];

	/**
	 * Crea una nuova istanza di ExpandSelect
	 *
	 * @param rootPropertiesToSelect - Proprietà root da selezionare (opzionale)
	 *
	 * @example
	 * // Senza selezione di proprietà root
	 * const expandSelect = new ExpandSelect();
	 *
	 * @example
	 * // Con proprietà root specifiche
	 * const expandSelect = new ExpandSelect('id,name,email,createdAt');
	 *
	 * @public
	 */
	constructor(rootPropertiesToSelect: string = '') {
		this._propertiesToSelect = rootPropertiesToSelect;
	}

	/**
	 * Imposta la relazione per questa istanza (uso interno)
	 * Converte la notazione con punti in slashes per OData
	 *
	 * @param relation - Nome della relazione
	 * @returns L'istanza corrente per method chaining
	 *
	 * @throws {TypeError} Se relation non è una stringa valida
	 *
	 * @example
	 * // Uso interno - non chiamare direttamente
	 * expandSelect._setRelation('customer.address');
	 * // _relation diventa 'customer/address'
	 *
	 * @private
	 */
	private _setRelation(relation: string): this {
		if (!relation || typeof relation !== 'string' || relation === '') {
			throw new TypeError('Parameter must be in a valid relation format');
		}
		this._relation = relation.replace(/\./g, '/');
		return this;
	}

	/**
	 * Imposta le proprietà root da selezionare
	 *
	 * @param rootPropertiesToSelect - Proprietà root da selezionare
	 * @returns L'istanza corrente per method chaining
	 *
	 * @example
	 * const expandSelect = new ExpandSelect()
	 *   .rootProperties('id,name,email');  // Seleziona solo questi campi root
	 *
	 * @example
	 * const expandSelect = new ExpandSelect()
	 *   .rootProperties('id,name,createdAt,updatedAt')
	 *   .expand('customer', 'id,companyName');
	 *
	 * @public
	 */
	public rootProperties(rootPropertiesToSelect: string): this {
		this._propertiesToSelect = rootPropertiesToSelect;
		return this;
	}

	/**
	 * Aggiunge un'espansione di relazione
	 * Supporta relazioni nidificate e selezione di proprietà specifiche
	 *
	 * @param relation - Nome della relazione da espandere (supporta notazione con punto)
	 * @param relationPropertiesToSelect - Proprietà della relazione da selezionare (opzionale)
	 * @returns L'istanza corrente per method chaining
	 *
	 * @example
	 * // Espansione semplice (tutti i campi)
	 * const expandSelect = new ExpandSelect()
	 *   .expand('customer');                    // $expand=customer
	 *
	 * @example
	 * // Espansione con selezione di campi
	 * const expandSelect = new ExpandSelect()
	 *   .expand('customer', 'id,name,email');   // $expand=customer($select=id,name,email)
	 *
	 * @example
	 * // Espansioni multiple
	 * const expandSelect = new ExpandSelect()
	 *   .expand('customer', 'id,companyName')
	 *   .expand('order.items', 'id,quantity,price')
	 *   .expand('category');
	 *
	 * @example
	 * // Espansioni nidificate profonde
	 * const expandSelect = new ExpandSelect()
	 *   .expand('order.customer.address', 'street,city,country');
	 *
	 * @public
	 */
	public expand(relation: string, relationPropertiesToSelect: string = ''): this {
		const relationExpand = new ExpandSelect(relationPropertiesToSelect)._setRelation(relation);
		this._relations.push(relationExpand);
		return this;
	}

	/**
	 * Aggiunge le espansioni e selezioni al contesto OData
	 * Costruisce la struttura $expand e $select per la query
	 *
	 * @param context - Contesto OData da modificare
	 *
	 * @example
	 * const context = {};
	 * const expandSelect = new ExpandSelect('id,name')
	 *   .expand('customer', 'id,companyName')
	 *   .expand('order.items');
	 *
	 * expandSelect._setContext(context);
	 * // context ora contiene:
	 * // {
	 * //   select: 'id,name',
	 * //   expand: {
	 * //     customer: { select: 'id,companyName' },
	 * //     order: {
	 * //       expand: {
	 * //         items: {}
	 * //       }
	 * //     }
	 * //   }
	 * // }
	 *
	 * @protected
	 */
	public _setContext(context: ODataContext): void {
		// Imposta la selezione di proprietà root se specificata
		if (
			this._propertiesToSelect &&
			typeof this._propertiesToSelect === 'string' &&
			this._propertiesToSelect !== ''
		) {
			context.select = this._propertiesToSelect;
		}

		// Costruisce le espansioni se presenti
		if (this._relations && Array.isArray(this._relations) && this._relations.length > 0) {
			if (!context.expand) {
				context.expand = {};
			}

			// Processa ogni relazione
			this._relations.forEach((relationExpand) => {
				const navigationProperties = relationExpand._relation.split('/');
				this._buildNestedExpansion(context.expand!, navigationProperties, relationExpand);
			});
		}
	}

	/**
	 * Costruisce ricorsivamente le espansioni nidificate
	 * Gestisce relazioni profonde come 'order.customer.address'
	 *
	 * @param expandObject - Oggetto expand corrente
	 * @param navigationProperties - Array dei nomi delle proprietà di navigazione
	 * @param relationExpand - Istanza ExpandSelect per questa relazione
	 *
	 * @example
	 * // Per 'order.customer.address' crea:
	 * // {
	 * //   order: {
	 * //     expand: {
	 * //       customer: {
	 * //         expand: {
	 * //           address: { select: '...' }
	 * //         }
	 * //       }
	 * //     }
	 * //   }
	 * // }
	 *
	 * @private
	 */
	private _buildNestedExpansion(
		expandObject: ExpandDefinition,
		navigationProperties: string[],
		relationExpand: ExpandSelect,
	): void {
		let currentExpand = expandObject;

		// Naviga attraverso le proprietà nidificate
		for (let i = 0; i < navigationProperties.length; i++) {
			const propertyName = navigationProperties[i];

			// Inizializza la proprietà se non esiste
			if (!currentExpand[propertyName]) {
				currentExpand[propertyName] = {};
			}

			// Se è l'ultima proprietà, imposta la selezione
			if (i === navigationProperties.length - 1) {
				// Imposta la selezione di proprietà se specificata
				if (
					relationExpand._propertiesToSelect &&
					typeof relationExpand._propertiesToSelect === 'string' &&
					relationExpand._propertiesToSelect !== ''
				) {
					currentExpand[propertyName].select = relationExpand._propertiesToSelect;
				}
			} else {
				// Altrimenti continua nidificando
				if (!currentExpand[propertyName].expand) {
					currentExpand[propertyName].expand = {};
				}
				currentExpand = currentExpand[propertyName].expand!;
			}
		}
	}

	/**
	 * Restituisce una descrizione testuale delle espansioni correnti
	 * Utile per debugging e logging
	 *
	 * @returns Stringa descrittiva delle espansioni
	 *
	 * @example
	 * const expandSelect = new ExpandSelect('id,name')
	 *   .expand('customer', 'id,companyName')
	 *   .expand('order.items');
	 *
	 * expandSelect.formatAsText();
	 * // "Select: id,name | Expand: customer(id,companyName), order.items"
	 *
	 * @public
	 */
	public formatAsText(): string {
		const parts: string[] = [];

		// Aggiunge informazioni sulla selezione root
		if (this._propertiesToSelect) {
			parts.push(`Select: ${this._propertiesToSelect}`);
		}

		// Aggiunge informazioni sulle espansioni
		if (this._relations.length > 0) {
			const expansions = this._relations.map((relation) => {
				const relationName = relation._relation.replace(/\//g, '.');
				if (relation._propertiesToSelect) {
					return `${relationName}(${relation._propertiesToSelect})`;
				}
				return relationName;
			});
			parts.push(`Expand: ${expansions.join(', ')}`);
		}

		return parts.length > 0 ? parts.join(' | ') : 'No expansions';
	}

	/**
	 * Rimuove tutte le espansioni e selezioni
	 * Resetta l'istanza allo stato iniziale
	 *
	 * @example
	 * const expandSelect = new ExpandSelect()
	 *   .expand('customer')
	 *   .expand('order');
	 *
	 * expandSelect.clear();
	 * // Tutte le espansioni rimosse
	 *
	 * @public
	 */
	public clear(): void {
		this._propertiesToSelect = '';
		this._relations = [];
	}

	/**
	 * Verifica se ci sono espansioni attive
	 *
	 * @returns true se non ci sono espansioni o selezioni, false altrimenti
	 *
	 * @example
	 * const expandSelect = new ExpandSelect();
	 * expandSelect.isEmpty(); // true
	 *
	 * expandSelect.expand('customer');
	 * expandSelect.isEmpty(); // false
	 *
	 * @public
	 */
	public isEmpty(): boolean {
		return !this._propertiesToSelect && this._relations.length === 0;
	}

	/**
	 * Restituisce il numero di espansioni attive
	 *
	 * @returns Numero di relazioni espanse
	 *
	 * @example
	 * const expandSelect = new ExpandSelect()
	 *   .expand('customer')
	 *   .expand('order.items');
	 *
	 * expandSelect.count(); // 2
	 *
	 * @public
	 */
	public count(): number {
		return this._relations.length;
	}

	/**
	 * Verifica se una relazione specifica è già espansa
	 *
	 * @param relation - Nome della relazione da controllare
	 * @returns true se la relazione è espansa, false altrimenti
	 *
	 * @example
	 * const expandSelect = new ExpandSelect()
	 *   .expand('customer')
	 *   .expand('order.items');
	 *
	 * expandSelect.hasExpansion('customer');     // true
	 * expandSelect.hasExpansion('order.items');  // true
	 * expandSelect.hasExpansion('category');     // false
	 *
	 * @public
	 */
	public hasExpansion(relation: string): boolean {
		const normalizedRelation = relation.replace(/\./g, '/');
		return this._relations.some((rel) => rel._relation === normalizedRelation);
	}

	/**
	 * Rimuove un'espansione specifica
	 *
	 * @param relation - Nome della relazione da rimuovere
	 * @returns true se la relazione è stata rimossa, false se non era presente
	 *
	 * @example
	 * const expandSelect = new ExpandSelect()
	 *   .expand('customer')
	 *   .expand('order.items');
	 *
	 * expandSelect.removeExpansion('customer'); // true - rimossa
	 * expandSelect.removeExpansion('category'); // false - non era presente
	 *
	 * @public
	 */
	public removeExpansion(relation: string): boolean {
		const normalizedRelation = relation.replace(/\./g, '/');
		const initialLength = this._relations.length;

		this._relations = this._relations.filter((rel) => rel._relation !== normalizedRelation);

		return this._relations.length < initialLength;
	}

	/**
	 * Restituisce l'elenco di tutte le relazioni espanse
	 *
	 * @returns Array dei nomi delle relazioni (con notazione punto)
	 *
	 * @example
	 * const expandSelect = new ExpandSelect()
	 *   .expand('customer')
	 *   .expand('order.items');
	 *
	 * expandSelect.getExpansions();
	 * // ['customer', 'order.items']
	 *
	 * @public
	 */
	public getExpansions(): string[] {
		return this._relations.map((rel) => rel._relation.replace(/\//g, '.'));
	}
}

/**
 * Factory function per creare espansioni e selezioni tipizzate
 * Compatibile con Vue 3 Composition API e completamente reattivo
 *
 * @param rootSelect - Proprietà root da selezionare (opzionale)
 * @returns Oggetto espansione tipizzato con API fluente
 *
 * @example
 * // Per Vue 3 Composition API
 * const dataExpand = ref(createExpandSelectData().expand('customer'));
 *
 * @example
 * // Con selezione e multiple espansioni
 * const complexExpand = ref(
 *   createExpandSelectData('id,name,createdAt')
 *     .expand('order.customer', 'id,companyName')
 *     .expand('activity')
 *     .expand('worker.user')
 * );
 */
export function createExpandSelectData(rootSelect?: string): ExpandSelectFactory {
	// Oggetto combinato con dati e metodi
	const expandSelect: ExpandSelectFactory = {
		rootSelect: rootSelect || null,
		expansions: [],

		expand(path: string, select?: string): ExpandSelectFactory {
			// Verifica se già esiste questa espansione
			const existingIndex = expandSelect.expansions.findIndex((exp) => exp.path === path);
			if (existingIndex >= 0) {
				// Aggiorna la selezione se fornita
				if (select) {
					expandSelect.expansions[existingIndex].select = select;
				}
			} else {
				// Aggiunge nuova espansione
				expandSelect.expansions.push({ path, select });
			}
			return expandSelect;
		},

		removeExpand(path: string): ExpandSelectFactory {
			const index = expandSelect.expansions.findIndex((exp) => exp.path === path);
			if (index >= 0) {
				expandSelect.expansions.splice(index, 1);
			}
			return expandSelect;
		},

		clear(): ExpandSelectFactory {
			expandSelect.expansions = [];
			return expandSelect;
		},

		select(fields: string): ExpandSelectFactory {
			expandSelect.rootSelect = fields;
			return expandSelect;
		},

		hasExpansions(): boolean {
			return expandSelect.expansions.length > 0;
		},
	};

	return expandSelect;
}
